import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import {
  AuthErrorMessage,
  checkIsSessionExpired,
  getAuthToken,
  setRequestedPath,
  setSessionExpireAtFront,
} from './auth';
import { getErrorInfo } from './getErrorInfo';
import { paths } from '@shared/enums/paths';

const buildConfig = (config: InternalAxiosRequestConfig, token: string): InternalAxiosRequestConfig => {
  config.headers = config.headers || {};
  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

export const paramsSerializer = (p: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();
  for (const key in p) {
    const value = p[key];
    if (Array.isArray(value)) {
      value.forEach((val) => searchParams.append(key, String(val)));
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }
  return searchParams.toString();
};

export const httpInterceptorsRequestUse = (axiosInstance: AxiosInstance): number =>
  axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await getAuthToken();

    if (token) {
      setSessionExpireAtFront();
      return buildConfig(config, token);
    }

    return config;
  });

export const httpInterceptorsResponseUse = (axiosInstance: AxiosInstance): number =>
  axiosInstance.interceptors.response.use(
    (resp) => resp,
    (error) => {
      const { response } = error;
      const status = response ? response.status : error.status;
      if (status === 500) {
        const message = response && response.data ? response.data.Message : error.message;
        Sentry.captureMessage(`${status}: ${message}`, 'error');
      }
      const errorInfo = getErrorInfo(error);
      const isSessionExpired = checkIsSessionExpired(errorInfo?.message ?? '');
      const notAuthHeader = errorInfo?.message === AuthErrorMessage.NoAuthHeader;
      const noSessionIdCookie = errorInfo?.message === AuthErrorMessage.NoSessionIdCookie;
      if (isSessionExpired || notAuthHeader || noSessionIdCookie) {
        if (window.location.pathname !== paths.login) {
          setRequestedPath(window.location.href);
          window.location.href = paths.login;
        }
      }

      return Promise.reject(error);
    }
  );
